import React, { useState, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, RoundedBox, Float, Line, ContactShadows, Text } from '@react-three/drei';
import * as THREE from 'three';

const ConnectionLine = ({ start, end }) => {
    return (
        <Line
            points={[start, end]}
            color="#94a3b8"
            lineWidth={1}
            transparent
            opacity={0.2}
        />
    );
};

const BarChart = ({ metrics }) => {
    return (
        <group position={[0, 0, 0.26]}>
            {metrics.map((metric, index) => {
                const height = (metric.value / metric.max) * 1.0;
                const xPos = (index - (metrics.length - 1) / 2) * 0.9;

                return (
                    <group key={index} position={[xPos, -0.6, 0]}>
                        {/* Bar Background */}
                        <mesh position={[0, 0.5, -0.05]}>
                            <planeGeometry args={[0.25, 1.0]} />
                            <meshBasicMaterial color="#ffffff" opacity={0.1} transparent />
                        </mesh>

                        {/* The Bar */}
                        <mesh position={[0, height / 2, 0]}>
                            <boxGeometry args={[0.25, height, 0.05]} />
                            <meshStandardMaterial color={metric.color} emissive={metric.color} emissiveIntensity={0.6} />
                        </mesh>

                        {/* Label */}
                        <Text
                            position={[0, -0.15, 0]}
                            fontSize={0.11}
                            color="#cbd5e1"
                            anchorX="center"
                            anchorY="top"
                        >
                            {metric.label}
                        </Text>

                        {/* Value */}
                        <Text
                            position={[0, height + 0.1, 0]}
                            fontSize={0.14}
                            fontWeight="bold"
                            color="white"
                            anchorX="center"
                            anchorY="bottom"
                        >
                            {metric.value}
                        </Text>
                    </group>
                );
            })}
        </group>
    );
};

const BlockNode = ({ id, data, onClick }) => {
    const [hovered, setHover] = useState(false);
    const mesh = useRef();

    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        if (mesh.current) {
            // Very subtle movement to keep text readable
            mesh.current.rotation.x = Math.cos(t * 0.2 + data.position[0]) * 0.01;
            mesh.current.rotation.y = Math.sin(t * 0.2 + data.position[1]) * 0.01;
        }
    });

    // Dimensions
    const boxWidth = 3.2;
    const boxHeight = 2.2;

    return (
        <Float speed={1.5} rotationIntensity={0.05} floatIntensity={0.2}>
            <group position={data.position} scale={1}>
                {/* The 3D Block */}
                <RoundedBox
                    ref={mesh}
                    args={[boxWidth, boxHeight, 0.2]} // Thinner block
                    radius={0.1}
                    smoothness={4}
                    onClick={(e) => { e.stopPropagation(); onClick(id); }}
                    onPointerOver={() => { document.body.style.cursor = 'pointer'; setHover(true); }}
                    onPointerOut={() => { document.body.style.cursor = 'auto'; setHover(false); }}
                >
                    <meshStandardMaterial
                        color="#0f172a" // Darker, richer blue-black
                        roughness={0.2}
                        metalness={0.6}
                        envMapIntensity={1.2}
                    />
                </RoundedBox>

                {/* Shadow */}
                <ContactShadows position={[0, -1.5, 0]} opacity={0.4} scale={6} blur={2} far={2} />

                {/* Content Container - Slightly offset from face to avoid z-fighting */}
                <group position={[0, 0, 0.15]}>

                    {/* Title Section */}
                    <Text
                        position={[0, 0.8, 0]}
                        fontSize={0.24}
                        fontWeight="900"
                        color="white"
                        anchorX="center"
                        anchorY="middle"
                        outlineWidth={0.01}
                        outlineColor="#000000"
                    >
                        {data.title}
                    </Text>
                    <Text
                        position={[0, 0.6, 0]}
                        fontSize={0.10}
                        fontWeight="bold"
                        color="#94a3b8"
                        anchorX="center"
                        anchorY="middle"
                        letterSpacing={0.05}
                    >
                        {data.subtitle}
                    </Text>

                    {/* Key Stat Badge */}
                    <group position={[0, 0.25, 0]}>
                        <mesh position={[0, 0, -0.01]}>
                            <planeGeometry args={[2.8, 0.35]} />
                            <meshBasicMaterial color={data.metrics[0].color} transparent opacity={0.15} />
                        </mesh>
                        <mesh position={[0, 0, -0.01]}>
                            <planeGeometry args={[2.8, 0.35]} />
                            <meshBasicMaterial color="black" transparent opacity={0.4} />
                        </mesh>
                        <Text
                            fontSize={0.22}
                            fontWeight="bold"
                            color={data.metrics[0].color}
                            anchorX="center"
                            anchorY="middle"
                        >
                            {data.keyStat}
                        </Text>
                    </group>

                    {/* 3D Chart */}
                    <BarChart metrics={data.metrics} />
                </group>

            </group>
            <ConnectionLine start={[0, 0, 0]} end={[data.position[0] * -1, data.position[1] * -1, 0]} />
        </Float>
    );
};

const CenterNode = ({ onClick }) => {
    const [hovered, setHover] = useState(false);

    return (
        <group position={[0, 0, 0]}>
            <Float speed={2} rotationIntensity={0.1} floatIntensity={0.2}>
                <group
                    onClick={(e) => { e.stopPropagation(); onClick("ANF"); }}
                    onPointerOver={() => { document.body.style.cursor = 'pointer'; setHover(true); }}
                    onPointerOut={() => { document.body.style.cursor = 'auto'; setHover(false); }}
                >
                    <group>
                        <mesh>
                            <octahedronGeometry args={[0.9, 0]} />
                            <meshStandardMaterial color="#f8fafc" roughness={0.1} metalness={0.8} />
                        </mesh>
                        <mesh>
                            <octahedronGeometry args={[0.92, 0]} />
                            <meshBasicMaterial color="#3b82f6" wireframe transparent opacity={0.2} />
                        </mesh>
                    </group>

                    <Html position={[0, 0, 1.2]} center pointerEvents="none" zIndexRange={[100, 0]}>
                        <div className={`bg-white/95 backdrop-blur-md px-6 py-3 rounded-xl shadow-xl border border-white/50 transition-transform duration-300 ${hovered ? 'scale-105' : 'scale-100'}`}>
                            <h3 className="text-sm font-black text-slate-800 tracking-widest m-0 select-none whitespace-nowrap">
                                EXPERIMENTS
                            </h3>
                        </div>
                    </Html>
                </group>
            </Float>
            <ContactShadows position={[0, -2.0, 0]} opacity={0.4} scale={8} blur={2} far={4} />
        </group>
    );
};

export const EvaluationModel = ({ onSelect, selectedId, data }) => {
    // Tighter positioning
    const positions = {
        DA: [-3.2, 1.6, 0],
        AR: [3.2, 1.6, 0],
        EV: [-3.2, -1.6, 0],
        NCF: [3.2, -1.6, 0],
        ANF: [0, 0, 0]
    };

    return (
        <group>
            <BlockNode id="DA" data={{ ...data.DA, position: positions.DA }} onClick={onSelect} />
            <BlockNode id="AR" data={{ ...data.AR, position: positions.AR }} onClick={onSelect} />
            <BlockNode id="EV" data={{ ...data.EV, position: positions.EV }} onClick={onSelect} />
            <BlockNode id="NCF" data={{ ...data.NCF, position: positions.NCF }} onClick={onSelect} />
            <CenterNode onClick={onSelect} />
        </group>
    );
};
